const EOF_BYTE = -1;
const EOF_CODE_POINT = -1;

class UTF8EncodeError extends Error {
    get [Symbol.toStringTag]() {
        return 'UTF8EncodeError';
    }
}
class UTF8DecodeError extends Error {
    get [Symbol.toStringTag]() {
        return 'UTF8DecodeError';
    }
}

function inRange(a, min, max) {
    return min <= a && a <= max;
}

function div(n, d) {
    return Math.floor(n / d);
}

function stringToCodePoints(string) {
    const cps = [];
    let i = 0;
    const n = string.length;

    while (i < string.length) {
        const c = string.charCodeAt(i);

        if (!inRange(c, 0xd800, 0xdfff)) {
            cps.push(c);
        } else if (inRange(c, 0xdc00, 0xdfff)) {
            cps.push(0xfffd);
        } else if (i === n - 1) {
            cps.push(0xfffd);
        } else {
            const d = string.charCodeAt(i + 1);

            if (inRange(d, 0xdc00, 0xdfff)) {
                const a = c & 0x3ff;
                const b = d & 0x3ff;

                i++;
                cps.push(0x10000 + (a << 10) + b);
            } else {
                cps.push(0xfffd);
            }
        }

        i++;
    }

    return cps;
}

function stringToUint8Array(str) {
    let pos = 0;
    const codePoints = stringToCodePoints(str);
    const outputBytes = [];

    while (codePoints.length > pos) {
        const codePoint = codePoints[pos++];

        if (inRange(codePoint, 0xd800, 0xdfff)) {
            throw new UTF8EncodeError(codePoint);
        } else if (inRange(codePoint, 0x0000, 0x007f)) {
            outputBytes.push(codePoint);
        } else {
            let count = 0;
            let offset = 0;

            if (inRange(codePoint, 0x0080, 0x07ff)) {
                count = 1;
                offset = 0xc0;
            } else if (inRange(codePoint, 0x0800, 0xffff)) {
                count = 2;
                offset = 0xe0;
            } else if (inRange(codePoint, 0x10000, 0x10ffff)) {
                count = 3;
                offset = 0xf0;
            }

            outputBytes.push(div(codePoint, 64 ** count) + offset);

            while (count > 0) {
                const temp = div(codePoint, 64 ** (count - 1));

                outputBytes.push(0x80 + (temp % 64));
                count--;
            }
        }
    }

    return new Uint8Array(outputBytes);
}

// eslint-disable-next-line
function uint8ArrayToString(uint8Array) {
    const fatal = false;
    let pos = 0;
    let result = '';
    let codePoint;
    let utf8CodePoint = 0;
    let utf8BytesNeeded = 0;
    let utf8BytesSeen = 0;
    let utf8LowerBoundary = 0;

    while (uint8Array.length > pos) {
        const byte = uint8Array[pos++];

        if (byte === EOF_BYTE) {
            if (utf8BytesNeeded !== 0) {
                codePoint = 0xfffd;

                const error = new UTF8DecodeError(codePoint);

                if (fatal) {
                    throw error;
                } else {
                    console.warn(error);
                }
            } else {
                codePoint = EOF_CODE_POINT;
            }
        } else if (utf8BytesNeeded === 0) {
            if (inRange(byte, 0x00, 0x7f)) {
                codePoint = byte;
            } else {
                if (inRange(byte, 0xc2, 0xdf)) {
                    utf8BytesNeeded = 1;
                    utf8LowerBoundary = 0x80;
                    utf8CodePoint = byte - 0xc0;
                } else if (inRange(byte, 0xe0, 0xef)) {
                    utf8BytesNeeded = 2;
                    utf8LowerBoundary = 0x800;
                    utf8CodePoint = byte - 0xe0;
                } else if (inRange(byte, 0xf0, 0xf4)) {
                    utf8BytesNeeded = 3;
                    utf8LowerBoundary = 0x10000;
                    utf8CodePoint = byte - 0xf0;
                } else {
                    const error = new UTF8DecodeError(codePoint);

                    if (fatal) {
                        throw error;
                    } else {
                        console.warn(error);
                    }
                }

                utf8CodePoint = utf8CodePoint * 64 ** utf8BytesNeeded;
                codePoint = null;
            }
        } else if (!inRange(byte, 0x80, 0xbf)) {
            utf8CodePoint = 0;
            utf8BytesNeeded = 0;
            utf8BytesSeen = 0;
            utf8LowerBoundary = 0;
            pos--;
            codePoint = byte || 0xfffd;

            const error = new UTF8DecodeError(codePoint);

            if (fatal) {
                throw error;
            } else {
                console.warn(error);
            }
        } else {
            utf8BytesSeen++;
            utf8CodePoint =
                utf8CodePoint +
                (byte - 0x80) * 64 ** (utf8BytesNeeded - utf8BytesSeen);

            if (utf8BytesSeen !== utf8BytesNeeded) {
                codePoint = null;
            } else {
                const cp = utf8CodePoint;
                const lowerBoundary = utf8LowerBoundary;

                utf8CodePoint = 0;
                utf8BytesNeeded = 0;
                utf8BytesSeen = 0;
                utf8LowerBoundary = 0;

                if (
                    inRange(cp, lowerBoundary, 0x10ffff) &&
                    !inRange(cp, 0xd800, 0xdfff)
                ) {
                    codePoint = cp;
                } else {
                    codePoint = byte || 0xfffd;

                    const error = new UTF8DecodeError(codePoint);

                    if (fatal) {
                        throw error;
                    } else {
                        console.warn(error);
                    }
                }
            }
        }

        if (codePoint !== null && codePoint !== EOF_CODE_POINT) {
            if (codePoint <= 0xffff) {
                if (codePoint > 0) {
                    result += String.fromCharCode(codePoint);
                }
            } else {
                codePoint -= 0x10000;
                result += String.fromCharCode(
                    0xd800 + ((codePoint >> 10) & 0x3ff),
                );
                result += String.fromCharCode(0xdc00 + (codePoint & 0x3ff));
            }
        }
    }

    return result;
}

export const UTF8 = {
    stringToUint8Array,
    uint8ArrayToString,
};
